import { useRef, useState, useEffect } from 'react';
import { useInView } from '@hooks/useScroll';
import { ChevronLeft, ChevronRight, Calendar, Plus } from 'lucide-react';
import { getPosts, loadSiteData, type NewsPost } from '@/lib/store';
import { useSiteText } from '@/lib/SiteTextContext';

export function NewsCarouselSection() {
  const { ref, isInView } = useInView();
  const { t } = useSiteText();
  const [posts, setPosts] = useState<NewsPost[]>(() => getPosts());
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const trackRef = useRef<HTMLDivElement>(null);

  // Load shared site data from the DS-style backend (/api/site-data) so admin
  // edits reach every visitor instantly (opcja 3: live API). Local edits still
  // take effect immediately while previewing.
  useEffect(() => {
    let alive = true;
    loadSiteData()
      .then((data) => { if (alive && data.posts?.length) setPosts(data.posts); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const scrollBy = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-card]');
    const step = card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const toggle = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section id="news-carousel" className="py-20 md:py-32 bg-foam-white overflow-hidden">
      <div className="container mx-auto px-6 md:px-12 lg:px-24">
        <div
          ref={ref}
          className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 transition-all duration-700 ease-sail ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-2xl">
            <p className="text-sm tracking-[0.3em] uppercase text-ocean-blue font-medium mb-4">
              {t('news.label')}
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-ocean-blue mb-4 text-wave">
              {t('news.title')}
            </h2>
            <p className="text-text-muted font-light">{t('news.text')}</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => scrollBy(-1)}
              aria-label="Poprzednie posty"
              className="group w-12 h-12 rounded-full border border-ocean-blue/20 text-ocean-blue flex items-center justify-center hover:bg-ocean-blue hover:text-white transition-colors duration-300"
            >
              <ChevronLeft className="w-5 h-5 transition-transform duration-300 group-hover:-translate-x-0.5" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              aria-label="Następne posty"
              className="group w-12 h-12 rounded-full border border-ocean-blue/20 text-ocean-blue flex items-center justify-center hover:bg-ocean-blue hover:text-white transition-colors duration-300"
            >
              <ChevronRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>

        <div
          ref={trackRef}
          className="flex gap-6 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory"
          style={{ scrollbarWidth: 'thin' }}
        >
          {posts.map((post, i) => {
            const isExpanded = !!expanded[post.id];
            return (
              <article
                key={post.id}
                data-card
                className="snap-start shrink-0 w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] bg-white rounded-3xl overflow-hidden shadow-lg border border-gray-100 flex flex-col transition-all duration-500 ease-sail hover:shadow-2xl hover:-translate-y-1.5"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-sail group-hover:scale-105"
                  />
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 text-xs text-white bg-black/40 px-2.5 py-1 rounded-full backdrop-blur-sm">
                    <Calendar className="w-3 h-3 text-accent-gold" />
                    {post.date}
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-display text-lg font-bold text-ocean-blue mb-3 leading-snug">
                    {post.title}
                  </h3>
                  {/* Smooth expand/collapse - grid-rows trick gives a fluid height
                      animation without measuring the DOM (no layout jumps). */}
                  <div
                    className={`grid transition-all duration-500 ease-sail ${
                      isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-70'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="text-sm text-text-muted font-light leading-relaxed">
                        {post.description}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggle(post.id)}
                    className="mt-auto pt-4 text-sm font-bold text-ocean-blue hover:text-accent-gold-dark transition-colors inline-flex items-center gap-1"
                  >
                    <Plus
                      className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-45' : ''}`}
                    />
                    {isExpanded ? 'Zwiń' : 'Czytaj więcej'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
